import React from 'react';

import { observer } from 'mobx-react';

import { GridLegacy as Grid, Typography } from '@mui/material';

import { Button } from 'components/Button';
import FieldHelpIcon from 'components/FieldHelpIcon';
import TimezonePicker from 'components/TimezonePicker';

import { useFocusAfterSave } from 'utils/hooks/useFocusAfterSave';
import { useRetryableLoad } from 'utils/hooks/useRetryableLoad';
import { useStore } from 'utils/hooks/store';

import {
  HeaderGrid,
  TaskRecordContainer,
  TaskRecordRow,
  Title,
} from 'containers/CompanySettings/MasterServiceAgreement/styles';
import {
  FieldColumn,
  FieldRow,
  FieldSkeleton,
  LabelColumn,
  LoadError,
  SettingsSection,
} from 'containers/Preferences/styles';

const HELP_TEXT = 'Applies to everyone who has not chosen their own timezone.';

const OrganisationTimezone: React.FunctionComponent = () => {
  const { timezoneSettings } = useStore();
  const { draft, status, saving, error } = timezoneSettings.organisation;
  const pickerRef = React.useRef<HTMLInputElement>(null);
  const focusAfterSave = useFocusAfterSave(saving);

  const retryLoad = useRetryableLoad(timezoneSettings.loadOrganisation);

  const handleChange = (zone: string): void => {
    focusAfterSave(pickerRef);
    timezoneSettings.saveOrganisation(zone);
  };

  return (
    <SettingsSection data-testid="company-settings-timezone-section">
      <HeaderGrid container alignItems="center">
        <Grid item xs={12} container alignContent="flex-start">
          <Title>Timezone</Title>
        </Grid>
      </HeaderGrid>
      <TaskRecordContainer>
        <TaskRecordRow>
          <FieldRow>
            <LabelColumn>
              Organisation timezone
              <FieldHelpIcon name="Organisation timezone" helpText={HELP_TEXT} />
            </LabelColumn>
            <FieldColumn>
              {status === 'loading' && <FieldSkeleton variant="rounded" />}
              {status === 'failed' && (
                <LoadError role="alert">
                  <Typography variant="body2" color="text.secondary">
                    The organisation timezone could not be loaded
                  </Typography>
                  <Button variant="outlined" size="small" onClick={retryLoad}>
                    Try again
                  </Button>
                </LoadError>
              )}
              {status === 'ready' && (
                <TimezonePicker
                  value={draft}
                  onChange={handleChange}
                  ariaLabel="Organisation timezone"
                  disabled={saving}
                  error={!!error}
                  helperText={error && <span data-testid="company-settings-timezone-error">{error}</span>}
                  inputRef={pickerRef}
                  data-testid="company-settings-timezone-dropdown"
                />
              )}
            </FieldColumn>
          </FieldRow>
        </TaskRecordRow>
      </TaskRecordContainer>
    </SettingsSection>
  );
};

export default observer(OrganisationTimezone);
